/**
 * Máquina de ESTADOS do caso de garantia/assistência (Task 15, brief 3.6 /
 * D-15.1). **Leia isto antes de mexer — é o coração da task.**
 *
 * Endereço (ADR-0009, exceção ENCERRADA em R5/FEAT-006, D-R3R5-11): esta é a
 * regra pura do RMA, PROMOVIDA de `apps/plataforma/src/rma/state-machine.ts`
 * para `packages/rma` (handoff H-15.A fechado). Como a máquina de pedido
 * (`packages/crm`), o servidor (`apps/api-dev` hoje, Edge Function depois) e o
 * app IMPORTAM `checkRmaTransition` DAQUI — não há uma segunda máquina no
 * servidor. O arquivo antigo em `apps/plataforma/src/rma/state-machine.ts`
 * passou a reexportar deste módulo (ADR-0004).
 *
 * Invariantes deste módulo:
 *
 * 1. **Puro e sem I/O.** Nada aqui faz fetch, lê env, toca DOM ou banco. É por
 *    isso que a MESMA função valida a transição no servidor e decide o que a
 *    tela oferece. Duas implementações dariam dois ciclos diferentes para o
 *    mesmo caso.
 * 2. **O vocabulário é o da persistência.** `RMA_STATES` e `RMA_RESOLUTIONS`
 *    são exatamente os `check (... in (...))` de `rma_cases` (migration 0034),
 *    e um teste compara os dois lados. Se o SQL e o código divergirem, o teste
 *    falha.
 * 3. **Avançar etapa exige a prova que a justifica (D-15.4).** Lavrar o
 *    protocolo sem nº de série, ou diagnosticar sem comprovante de compra, não
 *    é um avanço "incompleto": é uma transição INVÁLIDA. A recusa é explícita
 *    (a tela e o chamador precisam saber o que falta), nunca uma etapa gravada
 *    pela metade.
 * 4. **Reembolso/troca NÃO move dinheiro aqui.** A resolução gera a referência
 *    para o Financeiro (Task 16); esta máquina só registra a DECISÃO, não o
 *    pagamento (D-15.3).
 */
/** Etapas do ciclo de garantia/assistência (D-15.1). */
export const RMA_STATES = [
    "defect",
    "request",
    "protocol",
    "diagnosis",
    "ship_authorization",
    "received",
    "analysis",
    "resolution",
    "closed"
];
export const RMA_STATE_LABELS = {
    defect: "Defeito relatado",
    request: "Solicitação registrada",
    protocol: "Protocolo aberto",
    diagnosis: "Diagnóstico",
    ship_authorization: "Autorização de envio",
    received: "Recebido para análise",
    analysis: "Análise técnica",
    resolution: "Resolução",
    closed: "Encerrado"
};
/** Resultado final do caso (D-15.1). */
// `denied` = garantia NÃO PROCEDENTE (uso fora da especificação, dano por mau uso etc.):
// o caso é analisado e encerrado sem reparo/troca/reembolso. Migration 0050 amplia o check.
export const RMA_RESOLUTIONS = ["repair", "exchange", "return", "refund", "denied"];
export const RMA_RESOLUTION_LABELS = {
    repair: "Reparo",
    exchange: "Troca",
    return: "Devolução",
    refund: "Reembolso",
    denied: "Não procedente"
};
/**
 * Resoluções que GERAM referência para o Financeiro (Task 16): reembolso move
 * dinheiro de volta, troca gera o custo da nova peça. Reparo e devolução simples
 * não disparam o Financeiro por esta regra — a referência é opcional e o RMA
 * nunca executa o pagamento (D-15.3).
 */
export const RESOLUTIONS_WITH_FINANCIAL_REFERENCE = ["refund", "exchange"];
export function resolutionNeedsFinancialReference(resolution) {
    return RESOLUTIONS_WITH_FINANCIAL_REFERENCE.includes(resolution);
}
/**
 * Transições permitidas entre etapas.
 *
 * Escolhas que merecem explicação (D-15.1):
 * - **A linha principal é sequencial** (`defect → request → protocol →
 *   diagnosis → ship_authorization → received → analysis → resolution →
 *   closed`): cada etapa pressupõe a anterior (não se recebe peça sem autorizar
 *   o envio, não se resolve sem analisar). Pular etapa apagaria a prova que a
 *   etapa seguinte precisa.
 * - **`closed` é terminal**: o caso encerrado não volta. Uma reabertura é um
 *   NOVO caso (outro defeito, outra trilha), não a edição do histórico.
 */
export const ALLOWED_RMA_TRANSITIONS = {
    defect: ["request"],
    request: ["protocol"],
    protocol: ["diagnosis"],
    diagnosis: ["ship_authorization"],
    ship_authorization: ["received"],
    received: ["analysis"],
    analysis: ["resolution"],
    resolution: ["closed"],
    closed: []
};
/** Etapas finais: nada sai delas. */
export const TERMINAL_RMA_STATES = RMA_STATES.filter((state) => ALLOWED_RMA_TRANSITIONS[state].length === 0);
export function isRmaState(value) {
    return typeof value === "string" && RMA_STATES.includes(value);
}
export function isRmaResolution(value) {
    return typeof value === "string" && RMA_RESOLUTIONS.includes(value);
}
export function isTerminalRmaState(state) {
    return ALLOWED_RMA_TRANSITIONS[state].length === 0;
}
export function nextRmaStates(state) {
    return ALLOWED_RMA_TRANSITIONS[state];
}
export function canAdvanceRma(from, to) {
    return ALLOWED_RMA_TRANSITIONS[from].includes(to);
}
/**
 * Campos obrigatórios por etapa de destino (D-15.4).
 *
 * - `protocol` exige o **nº de série**: sem ele, o caso não identifica a peça
 *   física que está sob garantia.
 * - `diagnosis` exige o **comprovante de compra**: o diagnóstico de garantia
 *   depende da prova de que a compra está na cobertura.
 * - `resolution` exige o **resultado**: encerrar sem dizer reparo/troca/
 *   devolução/reembolso é fechar um caso sem conclusão.
 *
 * O nº de série e o comprovante, uma vez exigidos, continuam exigidos nas
 * etapas seguintes — mas como o caso só avança preenchendo-os no ponto de
 * entrada, a checagem por transição basta (o servidor persiste o valor).
 */
export const RMA_STATE_REQUIREMENTS = {
    protocol: ["serialNumber"],
    diagnosis: ["proofUrl"],
    resolution: ["resolution"]
};
export const RMA_FIELD_LABELS = {
    serialNumber: "Número de série",
    proofUrl: "Comprovante de compra",
    resolution: "Resultado",
    financialReference: "Referência financeira"
};
export function rmaRequirementsFor(to) {
    return RMA_STATE_REQUIREMENTS[to] ?? [];
}
/** Campos exigidos pela transição que não vieram preenchidos. */
export function missingRmaRequirements(to, input = {}) {
    const missing = [];
    for (const field of rmaRequirementsFor(to)) {
        const value = input[field];
        if (typeof value !== "string" || value.trim() === "")
            missing.push(field);
    }
    // A resolução reembolso/troca exige a referência financeira (D-15.3): sem ela
    // o RMA não teria para onde apontar o pagamento que não executa.
    if (to === "resolution" && isRmaResolution(input.resolution) && resolutionNeedsFinancialReference(input.resolution)) {
        const ref = input.financialReference;
        if (typeof ref !== "string" || ref.trim() === "")
            missing.push("financialReference");
    }
    return missing;
}
/**
 * Única porta de validação de transição do caso — usada pelo servidor (antes
 * de gravar) e pela tela (antes de oferecer). Devolve o motivo da recusa em vez
 * de lançar: quem chama precisa dizer ao operador O QUE falta.
 */
export function checkRmaTransition(from, to, input = {}) {
    if (!isRmaState(to))
        return { ok: false, error: "unknown_state", fields: ["to"] };
    if (!canAdvanceRma(from, to))
        return { ok: false, error: "invalid_rma_transition", fields: [from, to] };
    // Resultado presente mas fora do vocabulário é recusado antes de olhar os
    // demais campos (senão a referência financeira seria avaliada sobre lixo).
    if (to === "resolution" && input.resolution != null && !isRmaResolution(input.resolution)) {
        return { ok: false, error: "unknown_resolution", fields: ["resolution"] };
    }
    const missing = missingRmaRequirements(to, input);
    if (missing.length)
        return { ok: false, error: "rma_requirements_missing", fields: missing };
    return { ok: true };
}
